import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  Alert,
  List,
  Tag,
  Button,
  Progress,
} from 'antd';
import {
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
  StarOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { deliveryService } from '../services/deliveryService';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await deliveryService.getDashboardStats();

      if (response.data.success && response.data.data) {
        const dashboardData = response.data.data;
        setStats(dashboardData);
        setRecentOrders(dashboardData.recent_orders || []);
      } else {
        setError('Invalid dashboard data received');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      accepted: 'blue',
      picked_up: 'purple',
      in_transit: 'cyan',
      delivered: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      accepted: <CheckCircleOutlined />,
      picked_up: <ShoppingOutlined />,
      in_transit: <DollarOutlined />,
      delivered: <CheckCircleOutlined />,
      cancelled: <ExclamationCircleOutlined />,
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert message="Error" description={error} type="error" showIcon style={{ margin: '20px' }} />
    );
  }

  return (
    <div className="dashboard" style={{ padding: '16px' }}>
      <Title level={2} style={{ fontSize: '1.5rem', marginBottom: '16px' }}>
        Delivery Dashboard
      </Title>

      <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
        <Col xs={12} sm={12} md={6}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Total Orders"
              value={stats?.total_orders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '20px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Completed"
              value={stats?.completed_orders || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '20px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Out for Delivery"
              value={stats?.out_for_delivery || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Earnings"
              value={stats?.total_earnings || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1', fontSize: '20px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
        <Col xs={8} sm={8} md={8}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Wallet"
              value={stats?.wallet_balance || 0}
              prefix={<WalletOutlined />}
              precision={2}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Rating"
              value={stats?.rating || 0}
              prefix={<StarOutlined />}
              precision={1}
              valueStyle={{ color: '#faad14', fontSize: '18px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
            {stats?.rating && (
              <Progress
                percent={stats.rating * 20}
                showInfo={false}
                strokeColor="#faad14"
                style={{ marginTop: 8, maxWidth: '150px', margin: '8px auto 0' }}
                size="small"
              />
            )}
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card
            className="stats-card"
            size="small"
            style={{
              textAlign: 'center',
              minHeight: '100px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Statistic
              title="Avg Time"
              value={stats?.average_delivery_time || 0}
              suffix="min"
              prefix={<CarOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
              titleStyle={{ fontSize: '0.8rem' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Recent Orders" className="recent-orders-card">
            <List
              dataSource={Array.isArray(recentOrders) ? recentOrders : []}
              renderItem={(order) => (
                <List.Item
                  actions={[
                    <Button type="primary" size="small" onClick={() => navigate(`/orders`)}>
                      View Details
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={getStatusIcon(order.status)}
                    title={
                      <div>
                        <Text strong>Order #{order.id}</Text>
                        <Tag color={getStatusColor(order.status)} style={{ marginLeft: 8 }}>
                          {order.status?.replace('_', ' ').toUpperCase()}
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <Text strong>Customer: </Text>
                          <Text>{order.customer_name || 'N/A'}</Text>
                          {order.customer_phone && (
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                              ({order.customer_phone})
                            </Text>
                          )}
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <Text strong>Amount: </Text>
                          <Text>${order.amount || '0.00'}</Text>
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <Text strong>Delivery: </Text>
                          <Text type="secondary">{order.delivery_address || 'N/A'}</Text>
                        </div>
                        <div>
                          <Text type="secondary">
                            {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            {recentOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">No recent orders</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title="Quick Actions"
                className="quick-actions-card"
                extra={
                  <Button
                    type="text"
                    icon={<CheckCircleOutlined />}
                    onClick={fetchDashboardData}
                    title="Refresh Dashboard"
                  >
                    Refresh
                  </Button>
                }
              >
                <div className="quick-actions">
                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={() => navigate('/orders')}
                    style={{ marginBottom: '12px' }}
                    icon={<ShoppingOutlined />}
                  >
                    View All Orders
                  </Button>
                  <Button
                    block
                    size="large"
                    onClick={() => navigate('/earnings')}
                    style={{ marginBottom: '12px' }}
                    icon={<DollarOutlined />}
                  >
                    View Earnings
                  </Button>
                  <Button
                    block
                    size="large"
                    onClick={() => navigate('/location')}
                    style={{ marginBottom: '12px' }}
                    icon={<CarOutlined />}
                  >
                    Track Location
                  </Button>
                  <Button
                    block
                    size="large"
                    onClick={() => navigate('/notifications')}
                    icon={<ExclamationCircleOutlined />}
                  >
                    Notifications
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card title="Performance Summary" className="performance-card">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 16 }}>
                    <Progress
                      type="circle"
                      percent={
                        stats?.completed_orders && stats?.total_orders
                          ? Math.round((stats.completed_orders / stats.total_orders) * 100)
                          : 0
                      }
                      size={80}
                      strokeColor="#52c41a"
                    />
                  </div>
                  <Text strong>Completion Rate</Text>
                  <br />
                  <Text type="secondary">
                    {stats?.completed_orders || 0} of {stats?.total_orders || 0} orders
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
